import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  TAppointmentArrivalForm,
  appointmentArrivalSchema,
} from "./appointmentArrivalSchema";
import {
  FormContainer,
  FormInput,
  FormTabs,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import {
  createAppointmentArrival,
  getAppointmentArrivalById,
  updateAppointmentArrivalById,
} from "../../../../../services/appointmentArrival.service";
import { useNavigate, useParams } from "react-router-dom";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import { AppointmentArrivalConfigPage } from "./AppointmentArrivalConfig/AppointmentArrivalConfigPage";

const translationPath = "chatBot.page.service.appointmentArrivalRule.";

const AppointmentArrival = () => {
  const navigate = useNavigate();

  const { eI, id } = useParams();

  const { t } = useTranslation();

  const { setMessage } = useNotificationContext();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createAppointmentArrival,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateAppointmentArrivalById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getAppointmentArrivalById,
  );

  const saveHandler = (v: TAppointmentArrivalForm) =>
    save({
      ...v,
    });

  const updateHandler = (v: TAppointmentArrivalForm, id: string) =>
    update({
      item: {
        ...v,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TAppointmentArrivalForm>> => {
      const { data, success } = await get(id);
      if (data && success) {
        const newData: TAppointmentArrivalForm = {
          ...data,
        };
        return { data: newData, success: true };
      }

      return { data, success };
    },
    [get],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/chatBot/registrationsAndRules/appointmentArrivalRule/${data.id}/edit`,
            {
              replace: true,
            },
          );
        }
      } else {
        navigate(
          `/e/${eI}/chatBot/registrationsAndRules/appointmentArrivalRule/`,
        );
      }
    },
    [navigate, setMessage, t, id, eI],
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: t(translationPath + "appointment_arrival_config"),
        icon: <AccessTimeFilledIcon fontSize="small" />,
        component: <AppointmentArrivalConfigPage />,
      },
    ];

    return baseTabs;
  }, [t]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <>
      <FormContainer<TAppointmentArrivalForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "appointment_arrival")}
        schema={appointmentArrivalSchema}
        subtitle={t(translationPath + "new_appointment_arrival")}
        subtitleWatchField="name"
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight="fit-content"
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormInput
                size="small"
                name="name"
                fullWidth
                label={t(translationPath + "name")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="description"
                fullWidth
                multiline
                rows={4}
                label={t(translationPath + "description")}
              />
            </Grid>
          </Grid>
        )}
      />

      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AppointmentArrival;
